/**
 * InsightsDashboard - Dashboard de Insights para DamyxIA
 *
 * 🟥 CUADRO NEGRO 2026-02 — Refactor de tabs:
 *  - Estado `activeTab` en español: 'resumen' | 'tendencias' |
 *    'recomendaciones' | 'alertas'.
 *  - Cada tab renderiza su propio componente desde
 *    `components/insights/`. NO usa router, NO usa navigate(),
 *    NO usa useEffect para cambiar tabs.
 *  - Estilos `.tab` / `.tab-active` definidos en `index.css`.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Activity } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useLanguage } from '../context/LanguageContext';
import ResumenInsights from './insights/ResumenInsights';
import TendenciasInsights from './insights/TendenciasInsights';
import RecomendacionesInsights from './insights/RecomendacionesInsights';
import AlertasInsights from './insights/AlertasInsights';
import NarrativaInsights from './insights/NarrativaInsights';

const InsightsDashboardInner = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const es = language !== 'en';

  const [activeTab, setActiveTab] = useState('resumen');

  // 🟥 CUADRO NEGRO API Insights v1 (feb-2026): inyectar el userId
  // del usuario logueado en `window.__USER_ID__` para que los
  // componentes de cada tab puedan llamarlo en su `useInsights()`.
  useEffect(() => {
    if (!isOpen) return;
    try {
      window.__USER_ID__ = localStorage.getItem('userEmail') || 'guest';
    } catch (e) {
      window.__USER_ID__ = 'guest';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700 bg-gradient-to-r from-indigo-900/30 to-purple-900/30">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Activity className="w-6 h-6 text-indigo-400" />
              {es ? 'Dashboard de Insights' : 'Insights Dashboard'}
            </h2>
            <p className="text-sm text-zinc-400">
              {es ? 'Tu evolución e interacción con DamyxIA' : 'Your evolution and interaction with DamyxIA'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-700/50 rounded-lg transition-colors"
            data-testid="insights-close-btn"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 py-3 border-b border-zinc-700/50 bg-zinc-800/30 overflow-x-auto">
          <button
            className={activeTab === 'resumen' ? 'tab-active' : 'tab'}
            onClick={() => setActiveTab('resumen')}
            data-testid="insights-tab-resumen"
          >
            Resumen
          </button>

          <button
            className={activeTab === 'tendencias' ? 'tab-active' : 'tab'}
            onClick={() => setActiveTab('tendencias')}
            data-testid="insights-tab-tendencias"
          >
            Tendencias
          </button>

          <button
            className={activeTab === 'recomendaciones' ? 'tab-active' : 'tab'}
            onClick={() => setActiveTab('recomendaciones')}
            data-testid="insights-tab-recomendaciones"
          >
            Recomendaciones
          </button>

          <button
            className={activeTab === 'alertas' ? 'tab-active' : 'tab'}
            onClick={() => setActiveTab('alertas')}
            data-testid="insights-tab-alertas"
          >
            Alertas
          </button>

          <button
            className={activeTab === 'narrativa' ? 'tab-active' : 'tab'}
            onClick={() => setActiveTab('narrativa')}
            data-testid="insights-tab-narrativa"
          >
            Narrativa
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-testid="insights-content">
          {activeTab === 'resumen' && <ResumenInsights />}
          {activeTab === 'tendencias' && <TendenciasInsights />}
          {activeTab === 'recomendaciones' && <RecomendacionesInsights />}
          {activeTab === 'alertas' && <AlertasInsights />}
          {activeTab === 'narrativa' && <NarrativaInsights />}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// 🟥 CUADRO NEGRO MONITOREO (feb-2026) — Sentry Error Boundary.
// Si InsightsDashboard rompe (e.g. un sub-tab tira), el modal cae con un
// fallback decente en vez de colgar la UI. El error se reporta a
// `damyxia-frontend` automáticamente.
const InsightsDashboard = Sentry.withErrorBoundary(InsightsDashboardInner, {
  fallback: (
    <div
      role="alert"
      data-testid="insights-dashboard-error-fallback"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.7)',
        color: '#fafafa',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 12 }}>El Dashboard de Insights falló al cargar</h3>
        <p style={{ opacity: 0.8 }}>
          Ya reportamos el error a nuestro equipo. Cerrá esta ventana y volvé a intentarlo.
        </p>
      </div>
    </div>
  ),
});

export default InsightsDashboard;
